import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getCountryName } from '../../../lib/countries';

const countryCode = (import.meta.env.VITE_COUNTRY_CODE as string) || '';
const countryName = getCountryName(countryCode);

export default function ForgotPasswordPage() {
  const { user, resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await resetPasswordForEmail(email);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">CleanMyBed</div>
          <div className="login-subtitle">{countryName}</div>
        </div>

        {success ? (
          <div>
            <div className="alert alert-success mb-lg">
              <p className="m-0 fw-500">
                Check your email for a password reset link.
              </p>
            </div>
            <p style={{
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              textAlign: 'center',
              marginBottom: '1.5rem',
            }}>
              We&apos;ve sent a password reset link to <strong>{email}</strong>. Please check your inbox and follow the instructions.
            </p>
            <Link
              to="/login"
              className="btn btn-secondary"
              style={{ width: '100%', display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Back to Login
            </Link>
          </div>
        ) : (
          <>
            <p style={{
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              textAlign: 'center',
              marginBottom: '1.5rem',
            }}>
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="form-group">
                  <div className="form-error text-center">{error}</div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full mt-sm"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div style={{
              marginTop: '1.5rem',
              textAlign: 'center',
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-light)',
            }}>
              <Link to="/login" className="text-primary">
                Back to Login
              </Link>
            </div>
          </>
        )}

        <div className="login-footer">
          &copy; {new Date().getFullYear()} Sentinel&trade; Business Management
        </div>
      </div>
    </div>
  );
}
